
import { useState, useCallback } from 'react';
import { useAppSelector } from './useAppSelector';

export const useSubscription = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    feature: string;
    reason: 'limit_reached' | 'not_available';
  }>({ feature: '', reason: 'not_available' });

  const showSubscriptionModal = useCallback((feature: string, reason: 'limit_reached' | 'not_available' = 'not_available') => {
    setModalConfig({ feature, reason });
    setIsModalOpen(true);
  }, []);

  const checkPermission = useCallback((permission: keyof NonNullable<typeof user.subscription>) => {
    if (!user?.subscription) return false;
    return user.subscription[permission] === true;
  }, [user?.subscription]);

  const checkUsageLimit = useCallback((limit: 'number_of_queries_per_day' | 'dashboard_capacity') => {
    if (!user?.subscription || !user?.subscription_usage) return false;
    
    const currentUsage = user.subscription_usage[limit];
    const maxLimit = user.subscription[limit];
    
    return currentUsage < maxLimit;
  }, [user?.subscription, user?.subscription_usage]);

  return {
    isModalOpen,
    modalConfig,
    onCloseModal: useCallback(() => setIsModalOpen(false), []),
    showSubscriptionModal,
    canViewDashboard: useCallback(() => checkPermission('can_view_dashboard'), [checkPermission]),
    canViewCompetitorsDashboard: useCallback(() => checkPermission('can_view_competitors_dashboard'), [checkPermission]),
    canUploadProducts: useCallback(() => checkPermission('can_upload_products_file'), [checkPermission]),
    canExportDashboard: useCallback(() => checkPermission('can_export_dashboard'), [checkPermission]),
    canExportCompetitors: useCallback(() => checkPermission('can_export_competitors_dashboard'), [checkPermission]),
    hasQueryCapacity: useCallback(() => checkUsageLimit('number_of_queries_per_day'), [checkUsageLimit]),
    hasDashboardCapacity: useCallback(() => checkUsageLimit('dashboard_capacity'), [checkUsageLimit]),
    subscriptionName: user?.subscription?.name || 'No Subscription',
    subscriptionEndsAt: user?.subscription?.ends_at || null,
  };
};
